/**
 * JunctionBoxesTable Component
 *
 * Displays junction box information.
 */

import React, { useMemo } from 'react';
import { Circle } from 'lucide-react';
import { ReportTable, ReportSection, type ReportTableColumn } from '../primitives/ReportTable';
import type { JunctionBoxRow } from '@/types/reports';

interface JunctionBoxesTableProps {
  data: JunctionBoxRow[];
  groupByFloorPlan?: boolean;
}

export function JunctionBoxesTable({
  data,
  groupByFloorPlan = true,
}: JunctionBoxesTableProps) {
  const columns: ReportTableColumn<JunctionBoxRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        render: row => (
          <span className="font-medium">{row.name}</span>
        ),
        width: '100px',
      },
      {
        key: 'productName',
        header: 'Product',
        render: row => (
          <span className="text-muted-foreground">
            {row.productName ?? '-'}
          </span>
        ),
        hideOnMobile: true,
      },
      {
        key: 'connectedPathCount',
        header: 'Connected Paths',
        align: 'center',
        width: '120px',
      },
      {
        key: 'notes',
        header: 'Notes',
        render: row => (
          <span className="text-sm text-muted-foreground whitespace-pre-wrap">
            {row.notes ?? '-'}
          </span>
        ),
        hideOnMobile: true,
      },
    ],
    []
  );

  // Group data by floor plan if requested
  const groupedData = useMemo(() => {
    if (!groupByFloorPlan) {
      return [{ floorPlanName: null, items: data }];
    }

    const groups = new Map<string, JunctionBoxRow[]>();
    for (const row of data) {
      const key = row.floorPlanName;
      const existing = groups.get(key) ?? [];
      existing.push(row);
      groups.set(key, existing);
    }

    return Array.from(groups.entries()).map(([name, items]) => ({
      floorPlanName: name,
      items,
    }));
  }, [data, groupByFloorPlan]);

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Circle className="h-12 w-12 mb-4" />
        <p>No junction boxes found</p>
        <p className="text-sm">Add junction boxes to your floor plans to see them here</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groupedData.map((group, index) => (
        <ReportSection
          key={group.floorPlanName ?? index}
          title={groupByFloorPlan && group.floorPlanName ? group.floorPlanName : undefined}
          avoidPageBreak
        >
          <ReportTable
            data={group.items}
            columns={columns}
            keyExtractor={row => row.id}
            emptyMessage="No junctions in this floor plan"
            emptyIcon={<Circle className="h-8 w-8" />}
          />
        </ReportSection>
      ))}
    </div>
  );
}
